import type { QueryClient } from '@tanstack/react-query';
import type { DashboardStats } from '@ruta/shared';

/**
 * Header MiniPipeline counts (Awaiting Review, photos) live on this key.
 * Completing a review or clearing a photo must refresh it here — the visit
 * WebSocket may be late or missed, and staleTime is 30s (RUT-3668).
 */
export const DASHBOARD_STATS_QUERY_KEY = ['dashboard', 'stats'] as const;

export type WorkQueueStatField = 'visitsRequiringReview' | 'photosRequiringReview';

export function invalidateWorkQueueStats(queryClient: QueryClient) {
  return queryClient.invalidateQueries({ queryKey: DASHBOARD_STATS_QUERY_KEY });
}

/**
 * Drop the header badge immediately.
 *
 * Visits: refetch after the drop. `visitsRequiringReview` is a Dynamo GSI
 * COUNT, so GET /dashboard/stats matches the remaining set (including when
 * the left list is paged).
 *
 * Photos: do not refetch. `photosRequiringReview` is a Typesense facet
 * (`unreviewedPhotoCount`) that the visit stream processor writes after the
 * Dynamo review. An immediate refetch returns the old total and bounces the
 * badge (RUT-3668 leftover). The decrement is the remaining set — we already
 * know how many photos left.
 */
export function clearWorkQueueItems(
  queryClient: QueryClient,
  field: WorkQueueStatField,
  by = 1,
) {
  if (by > 0) {
    if (field === 'photosRequiringReview') {
      // Drop any in-flight stats fetch so a stale Typesense total cannot
      // overwrite the count we just wrote.
      void queryClient.cancelQueries({ queryKey: DASHBOARD_STATS_QUERY_KEY });
    }
    queryClient.setQueryData<DashboardStats>(DASHBOARD_STATS_QUERY_KEY, (old) => {
      if (!old) return old;
      return { ...old, [field]: Math.max(0, old[field] - by) };
    });
  }
  if (field === 'visitsRequiringReview') {
    return invalidateWorkQueueStats(queryClient);
  }
}
